import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Users, Mail, Lock, User, Phone, ArrowLeft, Loader2, Info, Key, Cake, GraduationCap, MapPin, UserPlus, Globe, CheckCircle, XCircle } from 'lucide-react';

import { Checkbox } from '../../components/ui/checkbox';

export default function RegisterTenantPage() {
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showExtendedFields, setShowExtendedFields] = useState(false);
  const { t, language, setLanguage, languages } = useLanguage();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    landlord_code: '',
    // Extended profile fields
    birthday: '',
    school_name: '',
    student_id: '',
    home_address: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    emergency_contact_address: '',
  });

  // Auto-fill landlord code from URL params
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      setFormData(prev => ({ ...prev, landlord_code: code.toUpperCase() }));
    }
  }, [searchParams]);
  
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('errors.passwordTooShort'));
      return;
    }

    // Validate landlord code
    if (!formData.landlord_code || formData.landlord_code.trim().length !== 6) {
      toast.error(t('auth.enterLandlordCode'));
      return;
    }

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      setSubmitting(true);
      const registrationData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'tenant',
        phone: formData.phone || null,
        landlord_code: formData.landlord_code.toUpperCase().trim(),
        // Extended profile data
        tenant_profile: {
          birthday: formData.birthday || null,
          school_name: formData.school_name || null,
          student_id: formData.student_id || null,
          home_address: formData.home_address || null,
          emergency_contact: formData.emergency_contact_name ? {
            name: formData.emergency_contact_name,
            relationship: formData.emergency_contact_relationship,
            phone: formData.emergency_contact_phone,
            address: formData.emergency_contact_address,
          } : null,
        },
      };
      
      await register(registrationData);

      toast.success(t('auth.registerSuccess') + '!');
      navigate('/tenant');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F0E8] to-[#E8E0D5] dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="tenant-register-form">
        <CardHeader className="text-center">
          {/* Language Switcher */}
          <div className="flex justify-end mb-2">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[120px] h-8 text-xs">
                <Globe className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.nativeName}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Link to="/register" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <div className="w-14 h-14 rounded-xl bg-[#6B8E6B]/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-[#6B8E6B]" />
          </div>
          <CardTitle className="text-2xl">{t('auth.register')} - {t('landing.forTenants')}</CardTitle>
          <CardDescription>
            {t('auth.createAccount')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">How it works:</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-600 dark:text-blue-400">
                  <li>Enter the 6-character code your landlord shared with you.</li>
                  <li>Create your account and add basic info.</li>
                  <li>Your landlord will verify you (usually within 24 hours) and assign your unit.</li>
                </ol>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Landlord Code Input */}
            <div className="space-y-2">
              <Label>{t('auth.landlordCode')} *</Label>
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Key className="w-4 h-4" />
                  <span>{t('auth.enterLandlordCode')}</span>
                </div>
                <div className="relative">
                  <Input
                    id="landlord_code"
                    name="landlord_code"
                    value={formData.landlord_code}
                    onChange={(e) => {
                      // Force uppercase + strip whitespace/specials as the user types
                      const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                      setFormData((prev) => ({ ...prev, landlord_code: v }));
                    }}
                    placeholder="e.g., ABC123"
                    className="text-center text-lg font-mono tracking-widest uppercase pr-9"
                    maxLength={6}
                    data-testid="landlord-code-input"
                    aria-invalid={formData.landlord_code.length > 0 && formData.landlord_code.length < 6}
                  />
                  {formData.landlord_code.length === 6 && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" data-testid="landlord-code-valid" />
                  )}
                  {formData.landlord_code.length > 0 && formData.landlord_code.length < 6 && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" data-testid="landlord-code-incomplete" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formData.landlord_code.length === 0
                    ? '6 characters · letters and numbers only.'
                    : formData.landlord_code.length < 6
                    ? `${6 - formData.landlord_code.length} more character${6 - formData.landlord_code.length === 1 ? '' : 's'} to go…`
                    : '✓ Code format looks good. We\'ll verify it when you submit.'}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  name="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="tenant-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  data-testid="tenant-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('auth.phone')} ({t('common.optional')})</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+63 912 345 6789"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10"
                  data-testid="tenant-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')} *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="tenant-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword')} *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="tenant-confirm-password-input"
                />
              </div>
            </div>

            {/* Extended Profile Fields Toggle */}
            <div className="border-t pt-4">
              <button
                type="button"
                onClick={() => setShowExtendedFields(!showExtendedFields)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full justify-center"
                data-testid="toggle-extended-fields"
              >
                <UserPlus className="w-4 h-4" />
                {showExtendedFields ? t('common.less') : t('common.more')} ({t('common.optional')})
              </button>
            </div>

            {/* Extended Profile Fields */}
            {showExtendedFields && (
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                <p className="text-xs text-muted-foreground">
                  {t('common.optional')}
                </p>

                {/* Date of Birth */}
                <div className="space-y-2">
                  <Label htmlFor="birthday">Date of Birth</Label>
                  <div className="relative">
                    <Cake className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="birthday"
                      name="birthday"
                      type="date"
                      value={formData.birthday}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="tenant-birthday-input"
                    />
                  </div>
                </div>

                {/* School / University */}
                <div className="space-y-2">
                  <Label htmlFor="school_name">School / University</Label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="school_name"
                      name="school_name"
                      placeholder="e.g., UP Diliman, Ateneo de Manila"
                      value={formData.school_name}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="tenant-school-input"
                    />
                  </div>
                </div>

                {/* Student ID Number */}
                <div className="space-y-2">
                  <Label htmlFor="student_id">Student ID Number</Label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="student_id"
                      name="student_id"
                      placeholder="e.g., 2024-12345"
                      value={formData.student_id}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="tenant-student-id-input"
                    />
                  </div>
                </div>

                {/* Complete Address */}
                <div className="space-y-2">
                  <Label htmlFor="home_address">Complete Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="home_address"
                      name="home_address"
                      placeholder="Street, City, Province, ZIP"
                      value={formData.home_address}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="tenant-home-address-input"
                    />
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {t('tenants.emergencyContact')}
                  </Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Input
                      name="emergency_contact_name"
                      placeholder={t('common.name')}
                      value={formData.emergency_contact_name}
                      onChange={handleChange}
                      data-testid="tenant-emergency-name-input"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        name="emergency_contact_relationship"
                        placeholder={t('tenants.relationship')}
                        value={formData.emergency_contact_relationship}
                        onChange={handleChange}
                        data-testid="tenant-emergency-relationship-input"
                      />
                      <Input
                        name="emergency_contact_phone"
                        placeholder={t('common.phone')}
                        value={formData.emergency_contact_phone}
                        onChange={handleChange}
                        data-testid="tenant-emergency-phone-input"
                      />
                    </div>
                    <Input
                      name="emergency_contact_address"
                      placeholder="Contact Address (Street, City, Province, ZIP)"
                      value={formData.emergency_contact_address}
                      onChange={handleChange}
                      data-testid="tenant-emergency-address-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox
                id="terms-agreement"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="terms-agreement-checkbox"
              />
              <label htmlFor="terms-agreement" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                I agree to the <Link to="/terms-of-service" target="_blank" className="text-[#C85A42] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" className="text-[#C85A42] hover:underline font-medium">Privacy Policy</Link>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#6B8E6B] hover:bg-[#5A7A5A]" 
              disabled={submitting || !formData.landlord_code || !agreedToTerms} 
              data-testid="tenant-register-submit"
            >
              {submitting ? t('common.loading') : t('auth.createAccount')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-4">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-[#6B8E6B] hover:underline font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
